export interface StyleData {
  id: number
  name: string
  /** The tag's own photo on the static host, or null — the text-only tile (BE-137). */
  image: string | null
  /** BE-137 — the cover's size ladder (`"<url> 480w, … 1792w"`) and width/height; null for a legacy cover. */
  imageSrcset?: string | null
  imageAspect?: number | null
  code?: string
  nameEn?: string
  completionPercent: number
  isFullyCompleted: boolean
  remainingSwipes: number
  swipesForCompletion: number
}

export interface ColorData {
  id: number
  name: string
  hex: string
  /** BE-144 */
  family?: string | null
}

export interface BrandData {
  id: number
  name: string
  logo: string
}

export interface RecommendationLink {
  id: string
  title: string
  url: string
  isExternal: boolean
}

export interface MyStylePageData {
  completionPercent: number
  tasteConfigId: string
  description: string
  styles: StyleData[]
  favoriteColors: ColorData[]
  favoriteBrands: BrandData[]
  recommendations: {
    title: string
    subtitle: string
    icon?: string
    links: RecommendationLink[]
  }
}

export interface AvailableStyle {
  id: number
  name: string
  image: string | null
  /** BE-137 — the cover's size ladder (`"<url> 480w, … 1792w"`) and width/height; null for a legacy cover. */
  imageSrcset?: string | null
  imageAspect?: number | null
  code?: string
  nameEn?: string
  completionPercent: number
  isFullyCompleted: boolean
  remainingSwipes: number
  swipesForCompletion: number
}

export interface AvailableColor {
  id: number
  name: string
  hex: string
  /** BE-144 — the colour family code (`neutrals` … `blacks`), null for an unmapped ML group. */
  family?: string | null
}

export interface AvailableBrand {
  id: number
  name: string
  logo: string
  /** BE-188 — one of the 30 most popular houses (the picker's Popular tab). */
  popular?: boolean
}

/** BE-144 — `GET /user/style/color-families` row, `name` in `Accept-Language`. */
export interface ColorFamily {
  code: string
  name: string
  sortOrder: number
}

/** BE-139 / BE-151 — the taste survey's own values. `never` clears the height on the server. */
export type HeelFrequency = 'never' | 'rarely' | 'sometimes' | 'often'
export type HeelHeight = 'up-to-4cm' | '5-7cm' | '8cm-plus'

export interface HeelPreferencePayload {
  frequency: HeelFrequency
  height?: HeelHeight | null
}

export interface HeelPreferenceResponse {
  frequency: string | null
  height: string | null
}

/**
 * `GET /user/style/dna` (§ 2.8) — the shopper's taste in one card. Camelised by the
 * interceptor. `summary` is ALWAYS null today (the AIRA narrative is not available);
 * every input with no data is an empty list; `updatedAt` is null before the first swipe.
 */
export interface UserStyleDnaStyle {
  styleId: number
  code: string
  name: string
  /** her preference normalised to her strongest style (0..1; 0 for a chosen style never swiped) */
  weight: number
  /** min(swipes, 30) / 30 * 100 */
  exploredPercent: number
  source: 'user' | 'photos' | string
  /** the model's confidence for a `photos` style; null for `user` */
  confidence: number | null
}

export interface UserStyleDna {
  styles: UserStyleDnaStyle[]
  colorsTop: { code: string; label: string; weight: number }[]
  brandsTop: { id: number; name: string; weight: number }[]
  /** `key` = `<tag type>.<tag code>` — category, fit, print, type, price_tier */
  attributesTop: { key: string; label: string; weight: number }[]
  /** the personalization confidence (0..1), the same number search personalises by */
  confidence: number
  summary: string | null
  updatedAt: string | null
  /* ——— BE-189 (additive) ——— */
  /** The cosine of wardrobe vs taste, 0..100; null with `matchReason` when it cannot be computed. */
  matchScore?: number | null
  matchReason?: string | null
  axes?: UserStyleDnaAxis[]
  bridge?: UserStyleDnaBridge | null
  /** AIRA's sentence — null today (`discrepancyReason: aira_text_not_wired`). */
  discrepancy?: string | null
  discrepancyReason?: string | null
  summaryReason?: string | null
  teaser?: { label: string; share: number } | null
  surveyCompleted?: boolean
}

/** BE-189 — one axis of «You like / You own» (`silhouette | colour | style`). */
export interface UserStyleDnaAxis {
  id: 'silhouette' | 'colour' | 'style'
  rows: Array<{
    key: string
    label: string
    /** 0..100 */
    like: number
    /** 0..100 — the wardrobe's share */
    own: number
    filter?: { styleId?: number; colorGroupId?: number } | null
  }>
}

export interface UserStyleDnaBridge {
  text: string | null
  reason?: string | null
  gaps: Array<{ axis: string; key: string; label: string; like: number; own: number; gap: number }>
  products: Array<{
    productId: number
    variantId: number
    brand: string
    name: string
    /** A static-host KEY (`farfetch/…`), or an absolute URL. */
    imageUrl: string | null
    price: string | null
  }>
}

/**
 * Inspiration photos (BE-140, § 3.3) under `/user/style/photos`. `photoId` (uuid) names a
 * PENDING upload and is consumed by the confirm; `id` (int) is the stored photo. Every
 * confirmed photo is `processing` with `reason: ml_style_inference_not_available` until the
 * inference service ships — the honest state, not a transient spinner.
 */
/**
 * BE-155 — `degraded`: ML could not analyse it (`reason` ∈ `ml_style_inference_not_available` |
 * `ml_rejected` | `ml_unavailable`); shown as "analysis unavailable", never as a spinner.
 */
export type InspirationPhotoStatus = 'processing' | 'ready' | 'failed' | 'degraded'

export interface InspirationPhoto {
  id: number
  /** presigned GET (1 h) or null */
  url: string | null
  status: InspirationPhotoStatus
  reason: string | null
  createdAt: string
}

export interface InspirationPhotosResponse {
  items: InspirationPhoto[]
  total: number
}

export interface InspirationPhotoUploadUrlResponse {
  photoId: string
  uploadUrl: string
  key: string
  expiresIn: number
}

/** The content types the upload-url accepts; anything else is a 400. */
export const INSPIRATION_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
/** `file_size` above this is a 400. */
/** BE-155 — `POST /user/style/photos/upload-url` takes up to 20 MB (400 above). */
export const INSPIRATION_PHOTO_MAX_BYTES = 20 * 1024 * 1024
